import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNotification } from '../../contexts/NotificationContext';
import historiaService from '../../services/historiaService';
import pacienteService from '../../services/pacienteService';
import especialidadService from '../../services/especialidadService';
import especialistaService from '../../services/especialistaService';
import { useAuth } from '../../hooks/useAuth';
import PageHeader from '../../components/common/PageHeader';

const HistoriaEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingData, setSavingData] = useState(false);
  const [paciente, setPaciente] = useState(null);
  const [especialidades, setEspecialidades] = useState([]);
  const [especialistas, setEspecialistas] = useState([]);
  const [filteredEspecialistas, setFilteredEspecialistas] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  // Esquema de validación
  const validationSchema = Yup.object({
    especialidad_id: Yup.number()
      .required('La especialidad es requerida'),
    especialista_id: Yup.number()
      .required('El especialista es requerido'),
    fecha_creacion: Yup.date()
      .required('La fecha de creación es requerida'),
    motivo_consulta: Yup.string()
      .required('El motivo de consulta es requerido'),
    // Los demás campos son opcionales
    diagnostico_cie10: Yup.string()
      .max(20, 'Máximo 20 caracteres'),
    num_sesiones_sugeridas: Yup.number()
      .integer('Debe ser un número entero')
      .positive('Debe ser un número positivo')
      .nullable(),
    estado: Yup.string()
      .required('El estado es requerido')
  });

  // Inicializar formik con valores vacíos
  const formik = useFormik({
    initialValues: {
      paciente_id: '',
      especialidad_id: '',
      especialista_id: '',
      fecha_creacion: new Date().toISOString().split('T')[0],
      motivo_consulta: '',
      historia_enfermedad_actual: '',
      antecedentes_personales: '',
      antecedentes_familiares: '',
      examen_mental: '',
      diagnostico: '',
      diagnostico_cie10: '',
      plan_tratamiento: '',
      num_sesiones_sugeridas: '',
      frecuencia_sesiones: '',
      objetivos_terapeuticos: '',
      pronostico: '',
      estado: 'Activo'
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setSavingData(true);
      setErrorMessage(null);
      try {
        await historiaService.updateHistoria(id, values);
        showNotification('Historia clínica actualizada con éxito', 'success');
        navigate(`/historias/${id}`);
      } catch (error) {
        console.error('Error updating historia clinica:', error);
        setErrorMessage(error.response?.data?.message || 'Error al actualizar la historia clínica');
        showNotification('Error al actualizar la historia clínica', 'error');
        setSavingData(false);
      }
    }
  });

  // Cargar datos iniciales
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        // Cargar historia clínica
        const historiaRes = await historiaService.getHistoria(id);
        const historia = historiaRes.data;
        
        // Cargar datos del paciente
        setPaciente(historia.paciente);
        
        // Cargar especialidades
        const especialidadesRes = await especialidadService.getEspecialidades();
        setEspecialidades(especialidadesRes.data);
        
        // Cargar especialistas
        const especialistasRes = await especialistaService.getEspecialistas();
        setEspecialistas(especialistasRes.data);
        
        // Filtrar especialistas por especialidad
        const filtered = especialistasRes.data.filter(especialista => 
          especialista.especialidades.some(esp => esp.id === historia.especialidad_id)
        );
        setFilteredEspecialistas(filtered);
        
        // Formatear fecha_creacion
        const formattedHistoria = {
          ...historia,
          fecha_creacion: historia.fecha_creacion ? new Date(historia.fecha_creacion).toISOString().split('T')[0] : null
        };
        
        // Actualizar formulario
        formik.setValues(formattedHistoria);
      } catch (error) {
        console.error('Error loading data:', error);
        setErrorMessage('Error al cargar los datos. Por favor, inténtelo nuevamente.');
        showNotification('Error al cargar los datos', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [id]);

  // Filtrar especialistas cuando cambia la especialidad seleccionada
  useEffect(() => {
    if (formik.values.especialidad_id && especialistas.length > 0) {
      const filtered = especialistas.filter(especialista => 
        especialista.especialidades.some(esp => esp.id === parseInt(formik.values.especialidad_id))
      );
      setFilteredEspecialistas(filtered);
    } else {
      setFilteredEspecialistas([]);
    }
  }, [formik.values.especialidad_id, especialistas]);

  // Pasos del formulario
  const steps = [
    {
      label: 'Información Básica',
      description: 'Seleccione especialidad, especialista y fecha',
      fields: ['especialidad_id', 'especialista_id', 'fecha_creacion']
    },
    {
      label: 'Motivo de Consulta e Historia',
      description: 'Detalle el motivo de consulta y la historia actual',
      fields: ['motivo_consulta', 'historia_enfermedad_actual']
    },
    {
      label: 'Antecedentes',
      description: 'Registre los antecedentes personales y familiares',
      fields: ['antecedentes_personales', 'antecedentes_familiares']
    },
    {
      label: 'Examen Mental',
      description: 'Documente el examen mental',
      fields: ['examen_mental']
    },
    {
      label: 'Diagnóstico',
      description: 'Establezca el diagnóstico y código CIE-10',
      fields: ['diagnostico', 'diagnostico_cie10']
    },
    {
      label: 'Plan de Tratamiento',
      description: 'Defina el plan de tratamiento y objetivos',
      fields: ['plan_tratamiento', 'num_sesiones_sugeridas', 'frecuencia_sesiones', 'objetivos_terapeuticos', 'pronostico', 'estado']
    }
  ];

  // Avanzar al siguiente paso
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Retroceder al paso anterior
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Verificar si el paso actual tiene errores
  const currentStepHasErrors = () => {
    const currentStepFields = steps[activeStep].fields;
    return currentStepFields.some(field => 
      formik.touched[field] && Boolean(formik.errors[field])
    );
  };

  // Verificar si todos los campos obligatorios del paso actual están completos
  const isCurrentStepComplete = () => {
    const currentStepFields = steps[activeStep].fields;
    return currentStepFields.every(field => {
      // Si el campo es requerido según el esquema de validación
      const isFieldRequired = validationSchema.fields[field] && 
        validationSchema.fields[field]._exclusive && 
        validationSchema.fields[field]._exclusive.required;
      
      // El paso está completo si el campo tiene valor (cuando es requerido) o no es requerido
      return !isFieldRequired || (formik.values[field] && formik.values[field] !== '');
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!paciente) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6" color="error" gutterBottom>
          Error al cargar la historia clínica
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/historias')}
        >
          Volver a Historias Clínicas
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="Editar Historia Clínica"
        subtitle={`Paciente: ${paciente.nombres} ${paciente.apellidos}`}
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Historias Clínicas', to: '/historias' },
          { text: 'Editar Historia Clínica' }
        ]}
        actionButton={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/historias/${id}`)}
          >
            Cancelar
          </Button>
        }
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Información del paciente */}
        <Grid item xs={12} md={3}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Paciente
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <img 
                  src={paciente.foto || '/static/images/avatar/default.jpg'} 
                  alt={paciente.nombres} 
                  style={{ 
                    width: 100, 
                    height: 100, 
                    borderRadius: '50%', 
                    objectFit: 'cover' 
                  }} 
                />
                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                  {paciente.nombres} {paciente.apellidos}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {paciente.edad} años • {paciente.sexo === 'M' ? 'Masculino' : paciente.sexo === 'F' ? 'Femenino' : 'Otro'}
                </Typography>
              </Box>
              
              <Typography variant="body2" paragraph>
                <strong>DNI:</strong> {paciente.dni}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>N° Historia:</strong> {paciente.numero_historia}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Fecha de Nacimiento:</strong> {new Date(paciente.fecha_nacimiento).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Teléfono:</strong> {paciente.telefono || 'No registrado'}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {paciente.email || 'No registrado'}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Progreso
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={(activeStep / steps.length) * 100} 
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                  <Typography variant="body2" color="textSecondary">
                    {Math.round((activeStep / steps.length) * 100)}%
                  </Typography>
                </Box>
              </Box>
              
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Paso {activeStep + 1} de {steps.length}
                </Typography>
                <Typography variant="body2" fontWeight="medium" mt={1}>
                  {steps[activeStep].label}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {steps[activeStep].description}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Formulario */}
        <Grid item xs={12} md={9}>
          <form onSubmit={formik.handleSubmit}>
            <Card>
              <CardContent>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel>
                        <Typography variant="subtitle1">{step.label}</Typography>
                      </StepLabel>
                      <StepContent>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                          {/* Paso 1: Información Básica */}
                          {index === 0 && (
                            <>
                              <Grid item xs={12} md={4}>
                                <FormControl 
                                  fullWidth 
                                  error={formik.touched.especialidad_id && Boolean(formik.errors.especialidad_id)}
                                >
                                  <InputLabel id="especialidad-label">Especialidad *</InputLabel>
                                  <Select
                                    labelId="especialidad-label"
                                    id="especialidad_id"
                                    name="especialidad_id"
                                    value={formik.values.especialidad_id}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    label="Especialidad *"
                                  >
                                    {especialidades.map(especialidad => (
                                      <MenuItem key={especialidad.id} value={especialidad.id}>
                                        {especialidad.nombre}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {formik.touched.especialidad_id && formik.errors.especialidad_id && (
                                    <FormHelperText>{formik.errors.especialidad_id}</FormHelperText>
                                  )}
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <FormControl 
                                  fullWidth 
                                  error={formik.touched.especialista_id && Boolean(formik.errors.especialista_id)}
                                  disabled={!formik.values.especialidad_id}
                                >
                                  <InputLabel id="especialista-label">Especialista *</InputLabel>
                                  <Select
                                    labelId="especialista-label"
                                    id="especialista_id"
                                    name="especialista_id"
                                    value={formik.values.especialista_id}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    label="Especialista *"
                                  >
                                    {filteredEspecialistas.map(especialista => (
                                      <MenuItem key={especialista.id} value={especialista.id}>
                                        {especialista.usuario.nombre} {especialista.usuario.apellidos}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {formik.touched.especialista_id && formik.errors.especialista_id && (
                                    <FormHelperText>{formik.errors.especialista_id}</FormHelperText>
                                  )}
                                  {formik.values.especialidad_id && filteredEspecialistas.length === 0 && (
                                    <FormHelperText>No hay especialistas disponibles para esta especialidad</FormHelperText>
                                  )}
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  id="fecha_creacion"
                                  name="fecha_creacion"
                                  label="Fecha de Creación *"
                                  type="date"
                                  value={formik.values.fecha_creacion}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.fecha_creacion && Boolean(formik.errors.fecha_creacion)}
                                  helperText={formik.touched.fecha_creacion && formik.errors.fecha_creacion}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                              </Grid>
                            </>
                          )}

                          {/* Paso 2: Motivo de Consulta e Historia */}
                          {index === 1 && (
                            <>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  id="motivo_consulta"
                                  name="motivo_consulta"
                                  label="Motivo de Consulta *"
                                  multiline
                                  rows={4}
                                  value={formik.values.motivo_consulta}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.motivo_consulta && Boolean(formik.errors.motivo_consulta)}
                                  helperText={formik.touched.motivo_consulta && formik.errors.motivo_consulta}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  id="historia_enfermedad_actual"
                                  name="historia_enfermedad_actual"
                                  label="Historia de la Enfermedad Actual"
                                  multiline
                                  rows={4}
                                  value={formik.values.historia_enfermedad_actual}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.historia_enfermedad_actual && Boolean(formik.errors.historia_enfermedad_actual)}
                                  helperText={formik.touched.historia_enfermedad_actual && formik.errors.historia_enfermedad_actual}
                                />
                              </Grid>
                            </>
                          )}

                          {/* Paso 3: Antecedentes */}
                          {index === 2 && (
                            <>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  id="antecedentes_personales"
                                  name="antecedentes_personales"
                                  label="Antecedentes Personales"
                                  multiline
                                  rows={6}
                                  value={formik.values.antecedentes_personales}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.antecedentes_personales && Boolean(formik.errors.antecedentes_personales)}
                                  helperText={formik.touched.antecedentes_personales && formik.errors.antecedentes_personales}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  id="antecedentes_familiares"
                                  name="antecedentes_familiares"
                                  label="Antecedentes Familiares"
                                  multiline
                                  rows={6}
                                  value={formik.values.antecedentes_familiares}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.antecedentes_familiares && Boolean(formik.errors.antecedentes_familiares)}
                                  helperText={formik.touched.antecedentes_familiares && formik.errors.antecedentes_familiares}
                                />
                              </Grid>
                            </>
                          )}

                          {/* Paso 4: Examen Mental */}
                          {index === 3 && (
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                id="examen_mental"
                                name="examen_mental"
                                label="Examen Mental"
                                multiline
                                rows={8}
                                value={formik.values.examen_mental}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.examen_mental && Boolean(formik.errors.examen_mental)}
                                helperText={formik.touched.examen_mental && formik.errors.examen_mental}
                              />
                            </Grid>
                          )}

                          {/* Paso 5: Diagnóstico */}
                          {index === 4 && (
                            <>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  id="diagnostico"
                                  name="diagnostico"
                                  label="Diagnóstico"
                                  multiline
                                  rows={4}
                                  value={formik.values.diagnostico}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.diagnostico && Boolean(formik.errors.diagnostico)}
                                  helperText={formik.touched.diagnostico && formik.errors.diagnostico}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  id="diagnostico_cie10"
                                  name="diagnostico_cie10"
                                  label="Código CIE-10"
                                  value={formik.values.diagnostico_cie10}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.diagnostico_cie10 && Boolean(formik.errors.diagnostico_cie10)}
                                  helperText={formik.touched.diagnostico_cie10 && formik.errors.diagnostico_cie10}
                                />
                              </Grid>
                            </>
                          )}

                          {/* Paso 6: Plan de Tratamiento */}
                          {index === 5 && (
                            <>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  id="plan_tratamiento"
                                  name="plan_tratamiento"
                                  label="Plan de Tratamiento"
                                  multiline
                                  rows={4}
                                  value={formik.values.plan_tratamiento}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.plan_tratamiento && Boolean(formik.errors.plan_tratamiento)}
                                  helperText={formik.touched.plan_tratamiento && formik.errors.plan_tratamiento}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  id="objetivos_terapeuticos"
                                  name="objetivos_terapeuticos"
                                  label="Objetivos Terapéuticos"
                                  multiline
                                  rows={3}
                                  value={formik.values.objetivos_terapeuticos}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.objetivos_terapeuticos && Boolean(formik.errors.objetivos_terapeuticos)}
                                  helperText={formik.touched.objetivos_terapeuticos && formik.errors.objetivos_terapeuticos}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  id="pronostico"
                                  name="pronostico"
                                  label="Pronóstico"
                                  multiline
                                  rows={3}
                                  value={formik.values.pronostico}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.pronostico && Boolean(formik.errors.pronostico)}
                                  helperText={formik.touched.pronostico && formik.errors.pronostico}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  id="num_sesiones_sugeridas"
                                  name="num_sesiones_sugeridas"
                                  label="Número de Sesiones Sugeridas"
                                  type="number"
                                  value={formik.values.num_sesiones_sugeridas}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.num_sesiones_sugeridas && Boolean(formik.errors.num_sesiones_sugeridas)}
                                  helperText={formik.touched.num_sesiones_sugeridas && formik.errors.num_sesiones_sugeridas}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  id="frecuencia_sesiones"
                                  name="frecuencia_sesiones"
                                  label="Frecuencia de Sesiones"
                                  placeholder="Ej: Semanal, Quincenal"
                                  value={formik.values.frecuencia_sesiones}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={formik.touched.frecuencia_sesiones && Boolean(formik.errors.frecuencia_sesiones)}
                                  helperText={formik.touched.frecuencia_sesiones && formik.errors.frecuencia_sesiones}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <FormControl 
                                  fullWidth 
                                  error={formik.touched.estado && Boolean(formik.errors.estado)}
                                >
                                  <InputLabel id="estado-label">Estado *</InputLabel>
                                  <Select
                                    labelId="estado-label"
                                    id="estado"
                                    name="estado"
                                    value={formik.values.estado}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    label="Estado *"
                                  >
                                    <MenuItem value="Activo">Activo</MenuItem>
                                    <MenuItem value="En pausa">En pausa</MenuItem>
                                    <MenuItem value="Finalizado">Finalizado</MenuItem>
                                  </Select>
                                  {formik.touched.estado && formik.errors.estado && (
                                    <FormHelperText>{formik.errors.estado}</FormHelperText>
                                  )}
                                </FormControl>
                              </Grid>
                            </>
                          )}
                        </Grid>

                        <Box sx={{ mb: 2, mt: 3 }}>
                          <div>
                            <Button
                              disabled={activeStep === 0}
                              onClick={handleBack}
                              sx={{ mr: 1 }}
                            >
                              Anterior
                            </Button>
                            {activeStep < steps.length - 1 ? (
                              <Button
                                variant="contained"
                                onClick={handleNext}
                                sx={{ mr: 1 }}
                                disabled={!isCurrentStepComplete() || currentStepHasErrors()}
                              >
                                Siguiente
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={savingData ? <CircularProgress size={24} /> : <SaveIcon />}
                                type="submit"
                                disabled={savingData || !isCurrentStepComplete() || currentStepHasErrors()}
                              >
                                {savingData ? 'Guardando...' : 'Actualizar Historia Clínica'}
                              </Button>
                            )}
                          </div>
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>

                {activeStep === steps.length && (
                  <Paper square elevation={0} sx={{ p: 3 }}>
                    <Typography>Todos los pasos completados. La historia clínica ha sido actualizada.</Typography>
                    <Button 
                      startIcon={<CheckIcon />}
                      onClick={() => navigate(`/historias/${id}`)} 
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Ver Historia Clínica
                    </Button>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HistoriaEdit;